import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Tab,
  Tabs,
} from "@mui/material"
import { useCallback, useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import EditContainer from "../../../components/global/editContainer"
import EditHeading from "../../../components/global/editHeading"
import PageSpinner from "../../../components/global/pageSpinner"
import { MainContext } from "../../../controllers/main"
import "../../../styles/channelEdit.scss"
import { LanguagesContext } from "../../../controllers/languages"
import { AddCircleRounded } from "@mui/icons-material"
import LoadingBackdrop from "../../../components/global/loadingBackdrop"
import CantPerformActionDialog from "../../../components/global/cantPerformActionDialog"
import { NewsQuizzesContext } from "../../../controllers/newsQuizzes"
import NewsQuizGeneralEdit from "../../../components/newsQuizzes/newsQuizGeneralEdit"
import NewsQuizTranslationsEdit from "../../../components/newsQuizzes/newsQuizTranslationsEdit"

const NewsQuizEdit = () => {
  const { animation, setAnimation, setChangesSaved, showPadding } =
    useContext(MainContext)
  const { languagesForEpisodeTabs } = useContext(LanguagesContext)
  const {
    loading: controllerLoading,
    setLoading: setControllerLoading,
    editMode,
    setEditMode,
    doneChanges,
    cancelChanges,
    currentNewsQuiz,
    getCurrentNewsQuiz,
    preChangesCurrentNewsQuiz,
    addTranslation,
    hasError,
    upsertNewsQuizParent,
    upsertNewsQuizDocument,
    deleteNewsQuiz,
  } = useContext(NewsQuizzesContext)
  const { quizId } = useParams()
  const navigate = useNavigate()

  // prevent tab close
  useEffect(() => {
    const handleTabClose = (event: any) => {
      event.preventDefault()
      console.log("beforeunload event triggered")
      return (event.returnValue = "Are you sure you want to exit?")
    }

    window.addEventListener("beforeunload", handleTabClose)

    return () => {
      window.removeEventListener("beforeunload", handleTabClose)
    }
  }, [])

  // loading
  const [loading, setLoading] = useState<boolean>(true)

  // page error
  const [pageError, setPageError] = useState<boolean>(false)

  // fetch current journey
  const fetchAll = async () => {
    const noErrors = await getCurrentNewsQuiz(quizId)
    if (noErrors) {
      setLoading(false)
      setControllerLoading(false)
    } else {
      setPageError(true)
    }
  }

  useEffect(() => {
    fetchAll()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // tabs
  const [selectedTab, setSelectedTab] = useState<number>(0)

  // add translation dialog
  const [addTranslationOpen, setAddTranslationOpen] = useState<boolean>(false)
  const [translationToAdd, setTranslationToAdd] = useState<
    { value: string; label: string; flag?: string } | undefined
  >()

  const handleAddTranslationClose = () => {
    setAddTranslationOpen(false)
    setTimeout(() => {
      setTranslationToAdd(undefined)
    }, 150)
  }

  // no default translation alert dialog
  const [
    noDefaultTranslationAlertDialogOpen,
    setNoDefaultTranslationAlertDialogOpen,
  ] = useState<boolean>(false)

  // delete news dialog
  const [deleteNewsDialogOpen, setDeleteNewsDialogOpen] =
    useState<boolean>(false)

  const handleDeleteNewsDialog = useCallback(() => {
    setDeleteNewsDialogOpen((current) => !current)
  }, [])

  return loading ? (
    <PageSpinner
      showBackButton={pageError}
      backButtonOnClick={() => {
        setAnimation(false)
        const pathname = window.location.pathname
        navigate(pathname.slice(0, pathname.lastIndexOf("/")))
      }}
    />
  ) : (
    <EditContainer>
      <EditHeading
        saveButtonLoading={controllerLoading}
        backButtonOnClick={() => {
          setAnimation(false)
          setTimeout(() => {
            const pathname = window.location.pathname
            navigate(pathname.slice(0, pathname.lastIndexOf("/")))
          }, 300)
        }}
        title={
          preChangesCurrentNewsQuiz.document.items.find(
            (documentItem) => documentItem.isDefault
          ).question
        }
        id={preChangesCurrentNewsQuiz.id}
        showButtons
        showEditButton
        showDeleteButton
        deleteButtonOnClick={handleDeleteNewsDialog}
        editModeActive={editMode}
        setEditModeActive={setEditMode}
        saveButtonDisabled={!doneChanges || hasError}
        undoButtonDisabled={!doneChanges}
        doneChanges={doneChanges}
        undoButtonOnClick={() => {
          if (selectedTab > preChangesCurrentNewsQuiz.document.items.length) {
            setSelectedTab(preChangesCurrentNewsQuiz.document.items.length)
          }
          cancelChanges()
        }}
        saveButtonOnClick={async () => {
          if (
            currentNewsQuiz.document.items.filter((item) => item.isDefault)
              .length
          ) {
            setControllerLoading(true)

            let noErrors = await upsertNewsQuizParent()

            if (noErrors) {
              noErrors = await upsertNewsQuizDocument()

              if (noErrors) {
                setControllerLoading(false)
                setChangesSaved(true)
              } else {
                setControllerLoading(false)
              }
            } else {
              setControllerLoading(false)
            }
          } else {
            setNoDefaultTranslationAlertDialogOpen(true)
          }
        }}
      />
      <Grow
        in={animation}
        timeout={300}
        style={{
          margin: showPadding === "yes" ? 16 : 0,
          marginTop: 0,
        }}
      >
        <Stack>
          {/* languages tabs */}
          <Stack direction="row" justifyContent="flex-start">
            <Card
              variant="outlined"
              style={{
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: showPadding === "yes" ? null : 0,
                borderBottom: "none",
                borderLeft: showPadding === "yes" ? null : "none",
              }}
            >
              <Stack direction="row" justifyContent="flex-start">
                <Tabs
                  value={selectedTab}
                  onChange={(e: any, newValue: number) => {
                    setSelectedTab(newValue)
                  }}
                  aria-label="languages tabs"
                >
                  <Tab label="General" style={{ minWidth: 180 }} />
                  {currentNewsQuiz.document.items.map((item) => (
                    <Tab
                      key={item.lang}
                      style={{ minWidth: 180 }}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>
                            {
                              languagesForEpisodeTabs.find(
                                (arrayItem) => arrayItem.value === item.lang
                              ).flag
                            }
                          </span>
                          {currentNewsQuiz.document.items.find(
                            (documentItem) => documentItem.lang === item.lang
                          ).isDefault ? (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <span>
                                {
                                  languagesForEpisodeTabs.find(
                                    (arrayItem) => arrayItem.value === item.lang
                                  ).label
                                }
                              </span>
                              <span style={{ fontSize: 10, marginTop: 2 }}>
                                (Default)
                              </span>
                            </Stack>
                          ) : (
                            <span>
                              {
                                languagesForEpisodeTabs.find(
                                  (arrayItem) => arrayItem.value === item.lang
                                ).label
                              }
                            </span>
                          )}
                        </Stack>
                      }
                    />
                  ))}
                </Tabs>
                <Divider orientation="vertical" variant="middle" flexItem />
                <div
                  style={{
                    minWidth: 70,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <IconButton
                    disabled={!editMode}
                    aria-label="add language button"
                    component="span"
                    style={{ width: 48, height: 48 }}
                    onClick={() => {
                      setAddTranslationOpen(true)
                    }}
                  >
                    <AddCircleRounded />
                  </IconButton>
                </div>
              </Stack>
            </Card>
          </Stack>
          <Card style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
            {!selectedTab ? (
              <NewsQuizGeneralEdit />
            ) : (
              <NewsQuizTranslationsEdit itemIndex={selectedTab - 1} />
            )}
          </Card>
        </Stack>
      </Grow>
      <LoadingBackdrop open={controllerLoading} />
      {/* add translation dialog */}
      <Dialog onClose={handleAddTranslationClose} open={addTranslationOpen}>
        <DialogTitle>Add translation</DialogTitle>
        {translationToAdd ? (
          <DialogContent>
            <DialogContentText id="alert-dialog-description-add-translation">
              Are you sure you want to add{" "}
              <b>{translationToAdd.label.toUpperCase()}</b>?
            </DialogContentText>
          </DialogContent>
        ) : languagesForEpisodeTabs.filter((item) => {
            if (
              item.value !== "general" &&
              !currentNewsQuiz.document.items.some(
                (translationItem) => translationItem.lang === item.value
              )
            ) {
              return item
            }
          }).length ? (
          <List sx={{ pt: 0, minWidth: 300 }}>
            {languagesForEpisodeTabs
              .filter((item) => {
                if (
                  item.value !== "general" &&
                  !currentNewsQuiz.document.items.some(
                    (translationItem) => translationItem.lang === item.value
                  )
                ) {
                  return item
                }
              })
              .map((item) => (
                <ListItem key={item.value} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      setTranslationToAdd(item)
                    }}
                  >
                    <Stack direction="row" spacing={1}>
                      <span>{item.flag}</span>
                      <span>{item.label.toUpperCase()}</span>
                    </Stack>
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        ) : (
          <DialogContent>
            <DialogContentText id="alert-dialog-description-add-translation">
              You have already added all languages available
            </DialogContentText>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleAddTranslationClose}>Cancel</Button>
          {translationToAdd && (
            <Button
              onClick={() => {
                addTranslation(translationToAdd.value)
                handleAddTranslationClose()
                setSelectedTab(currentNewsQuiz.document.items.length)
              }}
            >
              Add {translationToAdd.label}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* no default translation alert dialog */}
      <CantPerformActionDialog
        open={noDefaultTranslationAlertDialogOpen}
        setOpen={setNoDefaultTranslationAlertDialogOpen}
        content="There must be a default translation"
      />
      {/* delete news dialog */}
      <Dialog onClose={handleDeleteNewsDialog} open={deleteNewsDialogOpen}>
        <DialogTitle>Delete News</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description-add-translation">
            Are you sure you want to delete this Quiz? The operation is
            irreversible
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={controllerLoading} onClick={handleDeleteNewsDialog}>
            Cancel
          </Button>
          <Button
            disabled={controllerLoading}
            color="error"
            onClick={async () => {
              setControllerLoading(true)

              const noErrors = await deleteNewsQuiz(currentNewsQuiz.id)

              if (noErrors) {
                const pathname = window.location.pathname
                setTimeout(() => {
                  setControllerLoading(false)
                  navigate(pathname.slice(0, pathname.lastIndexOf("/")), {
                    state: { newsQuizDeleted: true },
                  })
                }, 1000)
              } else {
                handleDeleteNewsDialog()
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </EditContainer>
  )
}

export default NewsQuizEdit
